import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Tint = 'ai' | 'analyst' | 'sla' | 'automation' | 'portal' | 'help' | 'pricing' | 'dev' | 'hero' | 'cta';

const TINT_BLOBS: Record<Tint, string[]> = {
  hero:       ['radial-gradient(ellipse 60% 55% at 30% 40%, #BFD3DF 0%, transparent 70%)', 'radial-gradient(ellipse 50% 60% at 70% 70%, #EBC9AE 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 80% 20%, #B9C9B5 0%, transparent 70%)'],
  ai:         ['radial-gradient(ellipse 60% 55% at 25% 35%, #BFD3DF 0%, transparent 70%)', 'radial-gradient(ellipse 55% 50% at 75% 65%, #B9C9B5 0%, transparent 70%)', 'radial-gradient(ellipse 40% 45% at 60% 20%, #c7d9f5 0%, transparent 70%)'],
  analyst:    ['radial-gradient(ellipse 60% 55% at 30% 50%, #c0ccf5 0%, transparent 70%)', 'radial-gradient(ellipse 50% 60% at 70% 30%, #BFD3DF 0%, transparent 70%)', 'radial-gradient(ellipse 45% 40% at 50% 80%, #EBC9AE 0%, transparent 70%)'],
  sla:        ['radial-gradient(ellipse 55% 60% at 25% 45%, #f5e6c0 0%, transparent 70%)', 'radial-gradient(ellipse 60% 50% at 75% 60%, #B9C9B5 0%, transparent 70%)', 'radial-gradient(ellipse 40% 45% at 55% 20%, #EBC9AE 0%, transparent 70%)'],
  automation: ['radial-gradient(ellipse 55% 55% at 30% 40%, #dcc0f5 0%, transparent 70%)', 'radial-gradient(ellipse 50% 60% at 70% 65%, #BFD3DF 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 60% 20%, #B9C9B5 0%, transparent 70%)'],
  portal:     ['radial-gradient(ellipse 55% 55% at 25% 45%, #f5c0ce 0%, transparent 70%)', 'radial-gradient(ellipse 50% 55% at 72% 55%, #EBC9AE 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 55% 80%, #BFD3DF 0%, transparent 70%)'],
  help:       ['radial-gradient(ellipse 55% 55% at 30% 40%, #f5d0c0 0%, transparent 70%)', 'radial-gradient(ellipse 50% 55% at 70% 65%, #B9C9B5 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 60% 20%, #BFD3DF 0%, transparent 70%)'],
  pricing:    ['radial-gradient(ellipse 55% 55% at 25% 45%, #c0f5d0 0%, transparent 70%)', 'radial-gradient(ellipse 50% 55% at 72% 55%, #B9C9B5 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 55% 80%, #EBC9AE 0%, transparent 70%)'],
  dev:        ['radial-gradient(ellipse 55% 55% at 30% 40%, #c0c8d0 0%, transparent 70%)', 'radial-gradient(ellipse 50% 55% at 70% 65%, #BFD3DF 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 60% 20%, #B9C9B5 0%, transparent 70%)'],
  cta:        ['radial-gradient(ellipse 60% 55% at 30% 40%, #BFD3DF 0%, transparent 70%)', 'radial-gradient(ellipse 50% 60% at 70% 70%, #EBC9AE 0%, transparent 70%)', 'radial-gradient(ellipse 40% 40% at 80% 20%, #B9C9B5 0%, transparent 70%)'],
};

export function ArtPanel({ tint = 'hero', children, className }: { tint?: Tint; children?: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => {
      el.querySelectorAll<HTMLElement>('.ap-blob').forEach((b) => {
        b.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
      });
    }, { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const blobs = TINT_BLOBS[tint];

  return (
    <div ref={ref} className={cn('relative overflow-hidden rounded-2xl', className)} style={{ background: 'var(--art-ground)' }}>
      {/* Gradient blobs */}
      {blobs.map((gradient, i) => (
        <div
          key={i}
          className="ap-blob absolute inset-0"
          style={{
            background: gradient,
            filter: 'blur(70px)',
            animation: `ap-drift-${i % 3} ${18 + i * 5}s ease-in-out infinite alternate`,
            animationPlayState: 'paused',
          }}
        />
      ))}
      <div className="art-veil" />
      {/* SVG noise overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")", backgroundRepeat: 'repeat', backgroundSize: '128px 128px' }} />
      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
